import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { RotateCcw, Plus, Trash2, Calendar, ClipboardCheck, ShoppingCart, GraduationCap, Stethoscope, ListChecks, Users, AlertCircle, Sparkles, Loader2, Heart, Star, Lightbulb, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, addWeeks } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resetCategories = [
  { value: 'happening', label: 'What is happening this week?', icon: Calendar, color: 'bg-blue-100 text-blue-600' },
  { value: 'preparing', label: 'What needs preparing?', icon: ClipboardCheck, color: 'bg-purple-100 text-purple-600' },
  { value: 'buying', label: 'What needs buying?', icon: ShoppingCart, color: 'bg-green-100 text-green-600' },
  { value: 'school_deadlines', label: 'School/Admin deadlines?', icon: GraduationCap, color: 'bg-yellow-100 text-yellow-600' },
  { value: 'appointments', label: 'Appointments?', icon: Stethoscope, color: 'bg-pink-100 text-pink-600' },
  { value: 'routines', label: 'Routines to review?', icon: ListChecks, color: 'bg-indigo-100 text-indigo-600' },
  { value: 'delegated', label: 'Delegated tasks check?', icon: Users, color: 'bg-teal-100 text-teal-600' },
  { value: 'cracks', label: 'Anything falling through the cracks?', icon: AlertCircle, color: 'bg-red-100 text-red-600' },
];

const WeeklyResetPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItems, setNewItems] = useState({});
  const currentWeekStart = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
  
  // E7 Task F: AI Weekly Reset state
  const [aiResetLoading, setAiResetLoading] = useState(false);
  const [aiReset, setAiReset] = useState(null);
  const [aiCallId, setAiCallId] = useState(null);
  const [highlights, setHighlights] = useState('');

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/weekly-reset`);
      setItems(response.data.filter(i => i.week_start === currentWeekStart));
    } catch (error) {
      console.error('Error fetching weekly reset:', error);
    } finally {
      setLoading(false);
    }
  };

  const addItem = async (category) => {
    const content = newItems[category];
    if (!content?.trim()) return;
    
    try {
      await axios.post(`${API}/weekly-reset`, {
        week_start: currentWeekStart,
        category,
        item: content,
        is_checked: false
      });
      setNewItems({ ...newItems, [category]: '' });
      fetchItems();
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const toggleItem = async (item) => {
    try {
      await axios.put(`${API}/weekly-reset/${item.id}`, { ...item, is_checked: !item.is_checked });
      fetchItems();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/weekly-reset/${id}`);
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // E7 Task F: Get AI Weekly Reset reflection
  const getAIWeeklyReset = async () => {
    setAiResetLoading(true);
    setAiCallId(null);
    try {
      const response = await axios.post(`${API}/ai/weekly-reset`, {
        completed_count: checkedItems,
        total_count: totalItems,
        overdue_count: 0, // Could track this if we had due dates
        energy_levels: [], // Could integrate with wellness data
        highlight_moments: highlights ? highlights.split(',').map(h => h.trim()).filter(Boolean) : []
      });
      setAiReset(response.data.reset);
      setAiCallId(response.data.call_id || null);
    } catch (error) {
      toast.error('Failed to generate reflection');
    } finally {
      setAiResetLoading(false);
    }
  };

  // HITL: Confirm AI action
  const confirmAIAction = async (userAction) => {
    if (!aiCallId) return;
    try {
      await axios.post(`${API}/ai/confirm`, {
        call_id: aiCallId,
        user_action: userAction,
        edit_delta: []
      });
    } catch (error) {
      console.error('Failed to confirm AI action:', error);
    }
  };

  // Dismiss AI reset
  const dismissAIReset = async () => {
    await confirmAIAction('ignored');
    setAiReset(null);
    setAiCallId(null);
  };

  // Save AI reset
  const saveAIReset = async () => {
    await confirmAIAction('accepted');
    toast.success('Weekly reflection saved!');
    setAiReset(null);
    setAiCallId(null);
  };

  const getItemsByCategory = (category) => items.filter(i => i.category === category);
  
  const totalItems = items.length;
  const checkedItems = items.filter(i => i.is_checked).length;
  const progressPercent = totalItems > 0 ? (checkedItems / totalItems) * 100 : 0;

  const weekRange = `${format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'MMM d')} - ${format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'MMM d, yyyy')}`;

  return (
    <div className="space-y-6" data-testid="weekly-reset-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-nurture-navy flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
              <RotateCcw className="w-5 h-5 text-cyan-600" />
            </div>
            Weekly Reset
          </h1>
          <p className="text-nurture-muted mt-1">The habit that makes the system work</p>
        </div>
        <Badge variant="outline" className="px-4 py-2 text-base">
          <Calendar className="w-4 h-4 mr-2" />
          {weekRange}
        </Badge>
      </div>

      {/* Progress Card */}
      <Card className="rounded-3xl shadow-nurture bg-gradient-to-r from-cyan-50 to-teal-50 border-none">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-bold text-nurture-navy text-lg">Weekly Review Progress</h3>
              <p className="text-sm text-nurture-muted">Check off items as you review them</p>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold text-nurture-navy">{checkedItems}</span>
              <span className="text-nurture-muted">/{totalItems}</span>
            </div>
          </div>
          <Progress value={progressPercent} className="h-3" />
          {progressPercent === 100 && totalItems > 0 && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-nurture-sage font-medium mt-3 text-center"
            >
              Week reviewed! You're all set.
            </motion.p>
          )}
        </CardContent>
      </Card>

      {/* E7 Task F: AI Weekly Reflection Card */}
      <Card className="rounded-3xl shadow-nurture bg-gradient-to-r from-purple-50 to-pink-50 border-none">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <Heart className="w-4 h-4 text-purple-600" />
              </div>
              <h3 className="font-heading font-bold text-nurture-navy">AI Weekly Reflection</h3>
            </div>
            <Button
              onClick={getAIWeeklyReset}
              disabled={aiResetLoading}
              variant="outline"
              className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-100"
              data-testid="ai-weekly-reset-btn"
            >
              {aiResetLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              {aiResetLoading ? 'Reflecting...' : 'Get Reflection'}
            </Button>
          </div>
          
          {!aiReset && (
            <div className="space-y-2">
              <label className="text-sm text-purple-700">Share your highlights this week (optional):</label>
              <input
                type="text"
                value={highlights}
                onChange={(e) => setHighlights(e.target.value)}
                placeholder="e.g., Family dinner on Tuesday, finished project..."
                className="w-full p-2 rounded-xl border border-purple-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                data-testid="highlights-input"
              />
            </div>
          )}
          
          {aiReset && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              {/* Reflection */}
              {aiReset.reflection && (
                <div className="p-3 bg-white/70 rounded-xl">
                  <p className="text-nurture-navy text-sm">{aiReset.reflection}</p>
                </div>
              )}
              
              {/* Win Celebration */}
              {aiReset.win_celebration && (
                <div className="flex items-start gap-2 p-3 bg-green-50 rounded-xl">
                  <Star className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-green-700">{aiReset.win_celebration}</p>
                </div>
              )}
              
              {/* Gentle Nudge */}
              {aiReset.gentle_nudge && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl">
                  <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-700">{aiReset.gentle_nudge}</p>
                </div>
              )}
              
              {/* Affirmation */}
              {aiReset.affirmation && (
                <div className="p-4 bg-purple-100 rounded-xl text-center">
                  <p className="text-purple-800 font-medium italic">"{aiReset.affirmation}"</p>
                </div>
              )}
              
              {/* Next Week Focus */}
              {aiReset.next_week_focus && (
                <div className="p-3 bg-cyan-50 rounded-xl">
                  <p className="text-xs text-cyan-600 font-medium mb-1">Focus for next week:</p>
                  <p className="text-sm text-cyan-800">{aiReset.next_week_focus}</p>
                </div>
              )}
              
              {/* HITL Actions */}
              <div className="flex items-center gap-2 pt-2 border-t border-purple-200">
                <Button
                  onClick={saveAIReset}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                  data-testid="save-ai-reset-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Save Reflection
                </Button>
                <Button
                  variant="outline"
                  onClick={dismissAIReset}
                  data-testid="dismiss-ai-reset-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Dismiss
                </Button>
              </div>
            </motion.div>
          )}
        </CardContent>
      </Card>

      {/* Reset Categories */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-48 bg-nurture-cream-dark rounded-3xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {resetCategories.map((category, index) => {
            const Icon = category.icon;
            const categoryItems = getItemsByCategory(category.value);
            const categoryChecked = categoryItems.filter(i => i.is_checked).length;
            
            return (
              <motion.div
                key={category.value}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="rounded-3xl shadow-nurture h-full">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-nurture-navy flex items-center gap-2 text-base">
                      <div className={`w-8 h-8 rounded-lg ${category.color} flex items-center justify-center`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="flex-1">{category.label}</span>
                      {categoryItems.length > 0 && (
                        <Badge variant={categoryChecked === categoryItems.length ? 'default' : 'outline'} className={categoryChecked === categoryItems.length ? 'bg-nurture-sage' : ''}>
                          {categoryChecked}/{categoryItems.length}
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Existing items */}
                    <div className="space-y-2 mb-3">
                      {categoryItems.map((item) => (
                        <div
                          key={item.id}
                          className={`flex items-center gap-2 p-2 rounded-lg group ${
                            item.is_checked ? 'bg-nurture-sage/10' : 'bg-nurture-cream/50'
                          }`}
                        >
                          <Checkbox
                            checked={item.is_checked}
                            onCheckedChange={() => toggleItem(item)}
                            className="w-5 h-5"
                            data-testid={`reset-checkbox-${item.id}`}
                          />
                          <span className={`flex-1 text-sm ${item.is_checked ? 'line-through text-nurture-muted' : 'text-nurture-navy'}`}>
                            {item.item}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteItem(item.id)}
                            className="opacity-0 group-hover:opacity-100 h-6 w-6 text-nurture-muted hover:text-red-500"
                            data-testid={`delete-reset-${item.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    {/* Add new item */}
                    <div className="flex gap-2">
                      <Input
                        value={newItems[category.value] || ''}
                        onChange={(e) => setNewItems({ ...newItems, [category.value]: e.target.value })}
                        placeholder="Add item..."
                        className="rounded-xl text-sm h-9"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addItem(category.value);
                          }
                        }}
                        data-testid={`reset-input-${category.value}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => addItem(category.value)}
                        className="h-9 w-9 text-nurture-terracotta hover:text-nurture-terracotta-hover"
                        data-testid={`reset-add-${category.value}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Tips Section */}
      <Card className="rounded-3xl shadow-nurture">
        <CardContent className="p-6">
          <h3 className="font-heading font-bold text-nurture-navy mb-3">Weekly Reset Tips</h3>
          <ul className="space-y-2 text-sm text-nurture-muted">
            <li className="flex items-start gap-2">
              <span className="text-nurture-terracotta">•</span>
              Set a recurring time (e.g., Sunday evening) for your weekly reset
            </li>
            <li className="flex items-start gap-2">
              <span className="text-nurture-terracotta">•</span>
              Review each category even if there's nothing to add — confirmation reduces mental load
            </li>
            <li className="flex items-start gap-2">
              <span className="text-nurture-terracotta">•</span>
              Use this time to move Brain Dump items to the right places
            </li>
            <li className="flex items-start gap-2">
              <span className="text-nurture-terracotta">•</span>
              Check delegated tasks aren't slipping through the cracks
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default WeeklyResetPage;
